import { PALETTE } from "../core/Theme";
import { formatDuration } from "../core/TimeScale";
import type { Frame } from "./Frame";
import type { DrawingObject } from "../drawings/types";
import { rrInfo, isPositionTool } from "../drawings/types";
import { textBox, XY } from "../drawings/geometry";
import { handlePos, drawingPts } from "../drawings/hitTest";
import { outline, fmt, Label } from "../drawings/shapes";
import { volumeProfile } from "../drawings/analytics";
import { emojiImage } from "../drawings/emoji";

const BULL = "rgba(0,192,118,", BEAR = "rgba(255,59,48,";
const DARK = "rgba(11,14,20,0.92)";

function label(ctx: CanvasRenderingContext2D, l: Label, fallback: string) {
  const size = l.size ?? 10;
  ctx.font = `${size}px ${PALETTE.mono}`;
  ctx.textAlign = l.align ?? "left"; ctx.textBaseline = l.base ?? "middle";
  if (l.bg) {
    const w = ctx.measureText(l.text).width + 8;
    const bx = l.align === "right" ? l.x - w + 4 : l.align === "center" ? l.x - w / 2 : l.x - 4;
    ctx.fillStyle = l.bg === true ? DARK : l.bg; ctx.fillRect(bx, l.y - size / 2 - 3, w, size + 6);
  }
  ctx.fillStyle = l.color ?? (l.bg === true ? "#E5E7EB" : fallback); ctx.fillText(l.text, l.x, l.y);
}

function box(ctx: CanvasRenderingContext2D, lines: string[], cx: number, cy: number, color: string, size = 11) {
  ctx.font = `${size}px ${PALETTE.mono}`;
  const w = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 16, lh = size + 5, h = lines.length * lh + 10;
  const x = cx - w / 2, y = cy - h / 2;
  ctx.fillStyle = DARK; ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.setLineDash([]); ctx.strokeRect(x, y, w, h);
  ctx.fillStyle = "#E5E7EB"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  lines.forEach((l, i) => ctx.fillText(l, cx, y + 5 + lh * i + lh / 2));
}

function poly(ctx: CanvasRenderingContext2D, q: XY[], close = false) {
  ctx.beginPath(); ctx.moveTo(q[0].x, q[0].y);
  for (let i = 1; i < q.length; i++) ctx.lineTo(q[i].x, q[i].y);
  if (close) ctx.closePath();
}

function arrowHead(ctx: CanvasRenderingContext2D, from: XY, to: XY, s: number) {
  const ang = Math.atan2(to.y - from.y, to.x - from.x);
  ctx.beginPath(); ctx.moveTo(to.x, to.y);
  ctx.lineTo(to.x - s * Math.cos(ang - 0.45), to.y - s * Math.sin(ang - 0.45));
  ctx.lineTo(to.x - s * Math.cos(ang + 0.45), to.y - s * Math.sin(ang + 0.45));
  ctx.closePath(); ctx.fill();
}

function textCard(ctx: CanvasRenderingContext2D, at: XY, text: string, size: number, c: string, opaque: boolean) {
  const tb = textBox(ctx, at, text, size);
  if (opaque) { ctx.fillStyle = DARK; ctx.fillRect(tb.x, tb.y, tb.w, tb.h); ctx.strokeStyle = c; ctx.lineWidth = 1; ctx.strokeRect(tb.x, tb.y, tb.w, tb.h); }
  ctx.fillStyle = opaque ? "#E5E7EB" : c; ctx.textAlign = "left"; ctx.textBaseline = "top"; ctx.font = `${size}px ${PALETTE.mono}`;
  text.split("\n").forEach((l, i) => ctx.fillText(l, tb.x + 6, tb.y + 6 + i * (size + 4)));
  return tb;
}

let hoverPt: XY | null = null; // cursor position (plot px) for hover-only labels
let showInfo = false; // current object: pending/selected/hovered → show its info box

export function renderDrawings(f: Frame, list: DrawingObject[], selectedId: string | null, preview = false, hover: XY | null = null) {
  const { ctx, L } = f;
  hoverPt = hover;
  ctx.save();
  ctx.beginPath(); ctx.rect(0, 0, L.plotRight + L.rightAxis, L.priceBottom); ctx.clip();
  for (const d of list) {
    if (!d.visible) continue;
    const pts = drawingPts(f, d);
    if (!pts.length) continue;
    const c = d.style.color;
    ctx.strokeStyle = c; ctx.fillStyle = c; ctx.lineWidth = d.style.width; ctx.lineJoin = "round"; ctx.lineCap = "round";
    ctx.setLineDash(preview ? [4, 3] : d.style.dash);
    ctx.globalAlpha = 1;
    showInfo = preview || d.id === selectedId;
    drawOne(f, d, pts, c);
    ctx.setLineDash([]); ctx.globalAlpha = 1;
    if (d.id === selectedId && !preview) drawHandles(f, d, pts, c);
  }
  ctx.restore();
}

function drawOne(f: Frame, d: DrawingObject, pts: XY[], c: string) {
  const { ctx } = f;
  const dec = f.opts.decimals;
  const [a, b, e] = pts;
  const o = outline(f, d, pts);
  const fillAlpha = d.type === "highlighter" ? 0.35 : d.style.fill;
  for (const q of o.fills) { if (q.length < 3) continue; ctx.globalAlpha = fillAlpha; poly(ctx, q, true); ctx.fill(); ctx.globalAlpha = 1; }
  if (d.type === "highlighter") ctx.globalAlpha = 0.35;
  for (const q of o.lines) { if (q.length < 2) continue; poly(ctx, q); ctx.stroke(); }
  ctx.globalAlpha = 1;
  if (o.dashed?.length) { ctx.save(); ctx.setLineDash([4, 4]); ctx.globalAlpha = 0.7; ctx.lineWidth = Math.max(1, d.style.width - 0.5); for (const q of o.dashed) { if (q.length < 2) continue; poly(ctx, q); ctx.stroke(); } ctx.restore(); }
  for (const l of o.labels) label(ctx, l, c);
  ctx.fillStyle = c; ctx.strokeStyle = c;

  switch (d.type) {
    case "arrow": if (b) arrowHead(ctx, a, b, 6 + d.style.width * 2); break;
    case "path": if (pts.length >= 2) arrowHead(ctx, pts[pts.length - 2], pts[pts.length - 1], 6 + d.style.width * 2); break;
    case "arrowup": case "arrowdown": {
      const up = d.type === "arrowup", s = 9, y0 = a.y + (up ? 6 : -6), dir = up ? 1 : -1;
      ctx.beginPath(); ctx.moveTo(a.x, y0); ctx.lineTo(a.x - s, y0 + dir * s); ctx.lineTo(a.x - s / 2, y0 + dir * s); ctx.lineTo(a.x - s / 2, y0 + dir * s * 2); ctx.lineTo(a.x + s / 2, y0 + dir * s * 2); ctx.lineTo(a.x + s / 2, y0 + dir * s); ctx.lineTo(a.x + s, y0 + dir * s); ctx.closePath(); ctx.fill();
      break;
    }
    case "arrowmarkl": case "arrowmarkr": {
      const dir = d.type === "arrowmarkr" ? -1 : 1, s = 9, x0 = a.x + dir * 6;
      ctx.beginPath(); ctx.moveTo(x0, a.y); ctx.lineTo(x0 + dir * s, a.y - s); ctx.lineTo(x0 + dir * s, a.y - s / 2); ctx.lineTo(x0 + dir * s * 2, a.y - s / 2); ctx.lineTo(x0 + dir * s * 2, a.y + s / 2); ctx.lineTo(x0 + dir * s, a.y + s / 2); ctx.lineTo(x0 + dir * s, a.y + s); ctx.closePath(); ctx.fill();
      break;
    }
    case "flag": {
      ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(a.x, a.y - 26); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(a.x, a.y - 26); ctx.lineTo(a.x + 16, a.y - 21); ctx.lineTo(a.x, a.y - 14); ctx.closePath(); ctx.fill();
      break;
    }
    case "emoji": {
      const em = String(d.meta?.emoji ?? "🔥"), img = emojiImage(em), s = 28;
      if (img) ctx.drawImage(img, a.x - s / 2, a.y - s / 2, s, s);
      else { ctx.font = `${s - 4}px sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(em, a.x, a.y); }
      break;
    }
    case "text": textCard(ctx, a, d.text || "", d.style.fontSize, c, false); break;
    case "anchoredtext": textCard(ctx, a, d.text || "", d.style.fontSize, c, false); break;
    case "note": {
      ctx.beginPath(); ctx.arc(a.x, a.y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(a.x + 14, a.y - 18); ctx.stroke();
      textCard(ctx, { x: a.x + 14, y: a.y - 18 - 10 }, d.text || "Note", d.style.fontSize, c, true);
      break;
    }
    case "signpost": {
      ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(a.x, a.y - 40); ctx.stroke();
      ctx.beginPath(); ctx.arc(a.x, a.y, 3, 0, Math.PI * 2); ctx.fill();
      const tb = textBox(ctx, a, d.text || "", d.style.fontSize);
      textCard(ctx, { x: a.x - tb.w / 2, y: a.y - 40 - tb.h / 2 }, d.text || "", d.style.fontSize, c, true);
      break;
    }
    case "pricelabel": {
      const t = fmt(d.points[0].price, dec);
      ctx.font = `${d.style.fontSize}px ${PALETTE.mono}`;
      const w = ctx.measureText(t).width + 14, h = d.style.fontSize + 10, x = a.x + 12, y = a.y - h / 2;
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(x, y); ctx.lineTo(x + w, y); ctx.lineTo(x + w, y + h); ctx.lineTo(x, y + h); ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#0B0E14"; ctx.textAlign = "left"; ctx.textBaseline = "middle"; ctx.fillText(t, x + 7, a.y);
      break;
    }
    case "pricenote": {
      if (!b) break;
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      ctx.beginPath(); ctx.arc(a.x, a.y, 3, 0, Math.PI * 2); ctx.fill();
      textCard(ctx, b, `${fmt(d.points[0].price, dec)}${d.text ? `\n${d.text}` : ""}`, d.style.fontSize, c, true);
      break;
    }
    case "callout": {
      if (!b) break;
      const tb = textBox(ctx, b, d.text || "", d.style.fontSize);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(tb.x, tb.y + tb.h / 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(a.x, a.y, 3, 0, Math.PI * 2); ctx.fill();
      textCard(ctx, b, d.text || "", d.style.fontSize, c, true);
      break;
    }
    case "measure": {
      if (!b) break;
      const [p0, p1] = d.points;
      const up = p1.price >= p0.price;
      const col = up ? BULL : BEAR;
      const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y), w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
      ctx.fillStyle = col + "0.14)"; ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = col + "0.9)"; ctx.setLineDash([4, 3]); ctx.strokeRect(x, y, w, h); ctx.setLineDash([]);
      const hovered = !!hoverPt && hoverPt.x >= x - 6 && hoverPt.x <= x + w + 6 && hoverPt.y >= y - 6 && hoverPt.y <= y + h + 6;
      if (!showInfo && !hovered) break; // info box only while drawing, when selected, or on hover
      const dp = p1.price - p0.price, pc = p0.price ? (dp / p0.price) * 100 : 0, sign = dp >= 0 ? "+" : "";
      const points = Math.round(Math.abs(dp) * Math.pow(10, dec));
      const i0 = Math.round(f.fracIndexAt(a.x)), i1 = Math.round(f.fracIndexAt(b.x));
      const bars = Math.abs(i1 - i0);
      let ticks = 0; // tick count of the bars crossed (1s synthetic: 1 candle = 1 tick → ticks = bars); unknown on server candles
      for (let i = Math.max(0, Math.min(i0, i1)) + 1; i <= Math.min(f.candles.length - 1, Math.max(i0, i1)); i++) ticks += f.candles[i].volume || 0;
      box(ctx, [
        `${sign}${fmt(dp, dec)}  (${sign}${pc.toFixed(2)}%)  ·  ${points} pts`,
        `${ticks > 0 ? `${ticks} ticks · ` : ""}${bars} bars · ${formatDuration(Math.abs(p1.time - p0.time))}`,
      ], x + w / 2, up ? y - 24 : y + h + 24, col + "1)");
      break;
    }
    case "rr": case "longpos": case "shortpos": {
      if (!b) break;
      const info = rrInfo(d);
      const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x), w = x2 - x1;
      const ey = a.y, ty = b.y, sy = e ? e.y : a.y;
      ctx.fillStyle = BULL + "0.18)"; ctx.fillRect(x1, Math.min(ey, ty), w, Math.abs(ty - ey));
      ctx.fillStyle = BEAR + "0.18)"; ctx.fillRect(x1, Math.min(ey, sy), w, Math.abs(sy - ey));
      ctx.strokeStyle = BULL + "0.9)"; ctx.beginPath(); ctx.moveTo(x1, ty); ctx.lineTo(x2, ty); ctx.stroke();
      ctx.strokeStyle = BEAR + "0.9)"; ctx.beginPath(); ctx.moveTo(x1, sy); ctx.lineTo(x2, sy); ctx.stroke();
      ctx.strokeStyle = c; ctx.beginPath(); ctx.moveTo(x1, ey); ctx.lineTo(x2, ey); ctx.stroke();
      if (!info) break;
      label(ctx, { x: x2 + 6, y: ty, text: `Target ${fmt(info.target, dec)}  +${fmt(info.reward, dec)} pts`, color: "#00E68A" }, c);
      label(ctx, { x: x2 + 6, y: ey, text: `Entry ${fmt(info.entry, dec)}` }, c);
      label(ctx, { x: x2 + 6, y: sy, text: `Stop ${fmt(info.stop, dec)}  -${fmt(info.risk, dec)} pts`, color: "#FF5247" }, c);
      const lines = [`${d.type === "rr" ? info.side.toUpperCase() : d.type === "longpos" ? "LONG" : "SHORT"}  R:R 1 : ${info.ratio.toFixed(2)}`];
      if (info.lot != null) lines.push(`Size ${info.lot.toFixed(2)} lot · Risk $${info.riskAmt!.toFixed(0)} (${d.meta?.riskPct}%) · Reward $${info.rewardAmt!.toFixed(0)}`);
      box(ctx, lines, x1 + w / 2, Math.min(ey, ty) - 20, c);
      break;
    }
    case "fixedvp": case "anchoredvp": {
      const vp = volumeProfile(f, d.points[0], d.type === "fixedvp" ? d.points[1] ?? null : null);
      if (!vp) break;
      const x1 = f.x(vp.i1), x2 = d.type === "fixedvp" ? f.x(vp.i2) : f.L.plotRight, maxW = Math.max(30, (x2 - x1) * 0.6);
      ctx.globalAlpha = 0.25; ctx.fillRect(x1, f.y(vp.rows[vp.rows.length - 1].hi), x2 - x1, f.y(vp.rows[0].lo) - f.y(vp.rows[vp.rows.length - 1].hi)); ctx.globalAlpha = 1;
      vp.rows.forEach((r, k) => {
        const y1 = f.y(r.hi), y2 = f.y(r.lo), w = (r.vol / vp.max) * maxW, hh = Math.max(1, y2 - y1 - 1);
        ctx.fillStyle = k === vp.poc ? c : BULL + "0.55)"; ctx.fillRect(x1, y1, (r.up / (r.vol || 1)) * w, hh);
        ctx.fillStyle = k === vp.poc ? c : BEAR + "0.55)"; ctx.fillRect(x1 + (r.up / (r.vol || 1)) * w, y1, w - (r.up / (r.vol || 1)) * w, hh);
      });
      const pocRow = vp.rows[vp.poc];
      ctx.strokeStyle = c; ctx.setLineDash([3, 3]); ctx.beginPath(); ctx.moveTo(x1, f.y((pocRow.lo + pocRow.hi) / 2)); ctx.lineTo(x2, f.y((pocRow.lo + pocRow.hi) / 2)); ctx.stroke(); ctx.setLineDash([]);
      label(ctx, { x: x2 + 4, y: f.y((pocRow.lo + pocRow.hi) / 2), text: `POC ${fmt((pocRow.lo + pocRow.hi) / 2, dec)} · vol ${Math.round(vp.total)}`, bg: c, color: "#0B0E14" }, c);
      break;
    }
    default: break;
  }
}

function drawHandles(f: Frame, d: DrawingObject, pts: XY[], c: string) {
  const { ctx } = f;
  if (d.type === "brush" || d.type === "highlighter") { const q = pts; ctx.setLineDash([3, 3]); ctx.strokeStyle = c; ctx.lineWidth = 1; ctx.strokeRect(Math.min(...q.map((p) => p.x)) - 4, Math.min(...q.map((p) => p.y)) - 4, Math.max(...q.map((p) => p.x)) - Math.min(...q.map((p) => p.x)) + 8, Math.max(...q.map((p) => p.y)) - Math.min(...q.map((p) => p.y)) + 8); ctx.setLineDash([]); return; }
  for (let h = 0; h < pts.length; h++) {
    const p = handlePos(f, d, pts, h);
    ctx.beginPath(); ctx.arc(p.x, p.y, 4.5, 0, Math.PI * 2);
    ctx.fillStyle = d.locked ? "#7C879E" : "#0B0E14"; ctx.fill();
    ctx.lineWidth = 1.5; ctx.strokeStyle = d.locked ? "#7C879E" : c; ctx.stroke();
  }
}

export function renderSnapIndicator(f: Frame, snap: { x: number; y: number; label: string | null } | null) {
  if (!snap || !snap.label) return;
  const { ctx } = f;
  ctx.strokeStyle = PALETTE.amber; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(snap.x, snap.y, 6, 0, Math.PI * 2); ctx.stroke();
  label(ctx, { x: snap.x + 10, y: snap.y - 10, text: snap.label, bg: PALETTE.amber, color: "#0B0E14" }, PALETTE.amber);
}

export { isPositionTool };
